import styled from '@emotion/styled'
import * as React from 'react'

import { TColorKey } from '~theme/typings'
import { radiuses } from '~ui/styleGuide/radiuses'

import { Box, FlexCentered } from '../layout/Box'

type TStyledProps = {
  /**
   * badge size, *css lengths value*
   */
  size?: PropsOf<typeof Box>['height']
  /**
   * used to set the position of the badge, *css lengths value*
   */
  offsetTop?: PropsOf<typeof Box>['top']
  /**
   * used to set the position of the badge, *css lengths value*
   */
  offsetRight?: PropsOf<typeof Box>['right']
}

const propForwardOptions = {
  shouldForwardProp: (propName: string) =>
    propName !== `size` && propName !== `offsetTop` && propName !== `offsetRight`,
}

type TProps = RequiredChildren &
  TStyledProps & {
    /**
     * Content to display inside of a badge, e.g. Text component.
     * If `null`, badge will not render.
     */
    badgeContent: React.ReactNode
    /**
     * Background color of the badge.
     */
    bgColor?: TColorKey
  }

const StyledBadge = styled(FlexCentered, propForwardOptions)<TStyledProps>`
  --dimensions: ${({ size }) => size};

  position: absolute;
  height: var(--dimensions);
  width: var(--dimensions);
  top: ${({ offsetTop }) => offsetTop ?? 0};
  right: ${({ offsetRight }) => offsetRight ?? 0};
  border-radius: ${radiuses.rounded};
`

/**
 * Renders a badge with the provided content positioned absolutely
 * within the wrapper that is common to the both badge and children.
 * Position (top & right) of the badge can be set by `offset` prop.
 */
export const WithBadge: React.FC<TProps> = ({
  children,
  badgeContent,
  bgColor = `bgColors.card`,
  size = `1.5rem`,
  ...offset
}) => {
  if (!badgeContent) {
    return <>{children}</>
  }

  return (
    <Box position={`relative`}>
      {children}

      <StyledBadge bgColor={bgColor} shadow={`medium`} size={size} {...offset}>
        {badgeContent}
      </StyledBadge>
    </Box>
  )
}
